import { resolveApiUrl } from './apiBase';
import { isNativeApp } from './platform';

const TOKEN_KEY = 'preformiq_token';

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode */
  }
}

export function authHeaders(): HeadersInit {
  const t = getStoredToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function mediaUrl(path: string): string {
  if (path.startsWith('http')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return resolveApiUrl(p);
}

function handleUnauthorized(path: string, status: number) {
  if (status !== 401) return;
  const hadToken = !!getStoredToken();
  setStoredToken(null);
  if (hadToken && !path.startsWith('/api/auth/login') && !path.startsWith('/api/auth/signup')) {
    window.location.assign('/login');
  }
}

function parseJsonBody<T>(text: string, contentType: string | null): T {
  if (contentType?.includes('application/json') && text) {
    return JSON.parse(text) as T;
  }
  return undefined as T;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const res = await fetch(resolveApiUrl(path), {
    ...init,
    headers: {
      ...authHeaders(),
      ...(!isFormData && init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });

  handleUnauthorized(path, res.status);

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(text || res.statusText, res.status, text);
  }
  const ct = res.headers.get('content-type');
  if (ct?.includes('application/json')) return (await res.json()) as T;
  return undefined as T;
}

/**
 * Multipart upload. Uses XHR on native iOS — WKWebView `fetch` + FormData often dies with
 * "Load failed" even when the server is fine (large videos / nginx resets).
 */
export async function apiForm<T>(path: string, form: FormData, timeoutMs = 180_000): Promise<T> {
  if (!isNativeApp()) {
    const res = await fetch(resolveApiUrl(path), {
      method: 'POST',
      headers: { ...authHeaders() },
      body: form,
    });
    handleUnauthorized(path, res.status);
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(text || res.statusText, res.status, text);
    }
    const ct = res.headers.get('content-type');
    if (ct?.includes('application/json')) return (await res.json()) as T;
    return undefined as T;
  }

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', resolveApiUrl(path));
    const token = getStoredToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.timeout = timeoutMs;
    xhr.onload = () => {
      handleUnauthorized(path, xhr.status);
      const ct = xhr.getResponseHeader('content-type');
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(parseJsonBody<T>(xhr.responseText, ct));
        } catch (e) {
          reject(e instanceof Error ? e : new Error('Invalid JSON response'));
        }
        return;
      }
      reject(new ApiError(xhr.responseText || xhr.statusText || `HTTP ${xhr.status}`, xhr.status, xhr.responseText));
    };
    xhr.onerror = () => {
      reject(
        new ApiError(
          'Upload connection reset (often nginx body-size limit). On the server set client_max_body_size 100m;',
          0,
          undefined,
        ),
      );
    };
    xhr.ontimeout = () => {
      reject(new ApiError('Upload timed out. Try a shorter clip on Wi‑Fi.', 0, undefined));
    };
    xhr.send(form);
  });
}

export async function apiPublic<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(resolveApiUrl(path), {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(text || res.statusText, res.status, text);
  }
  return (await res.json()) as T;
}
